import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { portalApi, usePortal } from "@/lib/PortalContext";
import { ArrowRight, LogOut, Shield, X, UserCog } from "lucide-react";

const DISMISS_KEY = "luxe.portal.pwNudgeDismissed";

export default function PortalHomePage() {
  const { client, logout } = usePortal();
  const nav = useNavigate();
  const [projects, setProjects] = useState([]);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === "1"; } catch { return false; }
  });

  useEffect(() => {
    if (client === null) nav("/portal/login");
    if (client) portalApi.get("/portal/projects").then((r) => setProjects(r.data));
  }, [client, nav]);

  const dismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch {}
  };

  if (!client) return null;
  const showNudge = client.has_password === false && !dismissed;

  return (
    <div className="min-h-screen bg-[color:hsl(var(--background))]" data-testid="portal-home">
      <header className="border-b border-border">
        <div className="max-w-5xl mx-auto px-6 py-6 flex justify-between items-center">
          <div>
            <div className="overline text-[10px]" style={{ color: "#A68A64" }}>Subtend Studio</div>
            <div className="font-serif text-2xl leading-none">Media Walls</div>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/portal/account" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="link-portal-account">
              <UserCog className="w-3 h-3" strokeWidth={1.5}/> Account
            </Link>
            <button data-testid="btn-portal-logout" onClick={async () => { await logout(); nav("/"); }} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
              <LogOut className="w-3 h-3" strokeWidth={1.5}/> Sign out
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-6 py-16">
        <div className="overline">Welcome</div>
        <h1 className="font-serif font-light mt-3" style={{ fontSize: "clamp(38px, 5vw, 56px)", lineHeight: 1.05 }}>
          Hello {client.name?.split(" ")[0] || client.name}.
        </h1>
        <p className="text-muted-foreground mt-4 max-w-2xl">Your project details, current design and proposal are gathered here for you to review.</p>

        {showNudge && (
          <div className="luxe-card p-5 mt-10 flex items-start gap-4" data-testid="pw-nudge">
            <Shield className="w-6 h-6 mt-1 shrink-0" style={{ color: "#A68A64" }} strokeWidth={1.5}/>
            <div className="flex-1 min-w-0">
              <div className="font-serif text-xl">Create a password for faster sign-ins</div>
              <p className="text-sm text-muted-foreground mt-1">You can keep using magic links whenever you prefer — this is just an option.</p>
              <div className="mt-3 flex gap-2">
                <Link to="/portal/account" data-testid="btn-nudge-create" className="btn-primary text-xs">Create password</Link>
                <button data-testid="btn-nudge-dismiss" onClick={dismiss} className="btn-outline text-xs">Not now</button>
              </div>
            </div>
            <button onClick={dismiss} className="text-muted-foreground p-1" aria-label="Dismiss" data-testid="btn-nudge-close"><X className="w-4 h-4" strokeWidth={1.5}/></button>
          </div>
        )}

        <div className="mt-12 space-y-4">
          {projects.map((p) => (
            <Link key={p.id} to={`/portal/projects/${p.id}`} className="luxe-card p-6 flex items-center justify-between hover:border-foreground transition-colors" data-testid={`portal-project-${p.id}`}>
              <div>
                <div className="overline text-[10px]">{p.project_number}</div>
                <div className="font-serif text-2xl mt-1">{p.title}</div>
                <div className="text-sm text-muted-foreground mt-1">Stage: {p.delivery_stage || p.status}</div>
              </div>
              <ArrowRight className="w-5 h-5 text-muted-foreground" strokeWidth={1.5}/>
            </Link>
          ))}
          {projects.length === 0 && <div className="text-sm text-muted-foreground">No projects visible yet.</div>}
        </div>
      </main>
    </div>
  );
}
